import { useMemo, useState } from 'react';
import { Sheet } from '../components/Sheet';
import { RecoveryGrid } from '../components/RecoveryGrid';
import { IconFlame, IconInfo } from '../components/Icons';
import { getExercise } from '../data/exercises';
import { MUSCLES } from '../data/muscles';
import { describeBlock, describeBlockChange } from '../engine/blocks';
import { generateWorkout } from '../engine/generator';
import { planDay, weekAhead, type SessionOption } from '../engine/planner';
import { mostOverdue } from '../engine/recovery';
import { dayShort, todayIso } from '../lib/dates';
import { useApp } from '../store/AppStore';
import type { SessionTemplate } from '../data/splits';

export function Today({ onStarted, onGoCardio }: { onStarted: () => void; onGoCardio: () => void }) {
  const { state, startWorkout } = useApp();
  const [showRecovery, setShowRecovery] = useState(false);
  const [preview, setPreview] = useState<SessionTemplate | null>(null);
  const [showAlternatives, setShowAlternatives] = useState(false);

  const today = todayIso();
  const plan = useMemo(() => planDay(state, today), [state, today]);
  const week = useMemo(() => weekAhead(state, today), [state, today]);
  const overdue = useMemo(() => mostOverdue(plan.statuses, 4), [plan.statuses]);
  const todayHealth = state.healthDays.find((d) => d.date === today);

  const begin = (template: SessionTemplate) => {
    startWorkout(template);
    setPreview(null);
    setShowAlternatives(false);
    onStarted();
  };

  const blockChange = describeBlockChange(plan.block);

  return (
    <div className="stack">
      {/* ---- where you are in the program ---- */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 9 }}>
          <span className="tiny muted">{describeBlock(plan.block)}</span>
          {plan.block.isDeload && <span className="pill warn">Deload</span>}
        </div>
        <div className="segbar">
          {Array.from({ length: plan.block.blockLengthWeeks }, (_, i) => (
            <span
              key={i}
              className={
                i + 1 === plan.block.weekInBlock
                  ? plan.block.isDeload
                    ? 'deload'
                    : 'on'
                  : i + 1 < plan.block.weekInBlock
                    ? 'on'
                    : ''
              }
            />
          ))}
        </div>
        {blockChange && <div className="tiny faint" style={{ marginTop: 8 }}>{blockChange}</div>}
      </div>

      {/* ---- the week ---- */}
      <div className="weekstrip">
        {week.map((d) => (
          <div className={`day${d.isToday ? ' today' : ''}`} key={d.date}>
            <div className="name">{dayShort(d.dayNumber)}</div>
            <div className={`mark ${d.kind}`}>
              {d.done ? '✓' : d.kind === 'lift' ? '●' : d.kind === 'cardio' ? '▲' : '–'}
            </div>
          </div>
        ))}
      </div>

      {/* ---- activity from Apple Health ---- */}
      {todayHealth && (
        <div className="card">
          <div className="row between" style={{ marginBottom: 10 }}>
            <span className="card-title" style={{ margin: 0 }}>
              Today's movement
            </span>
            <span className="tiny faint">Apple Health</span>
          </div>
          <div className="grid-3">
            <div className="stat">
              <div className="label">Steps</div>
              <div className="value">
                {typeof todayHealth.steps === 'number'
                  ? todayHealth.steps.toLocaleString('en-GB')
                  : '–'}
              </div>
            </div>
            <div className="stat">
              <div className="label">Active</div>
              <div className="value">
                {todayHealth.activeEnergyKcal ?? '–'}
                <span className="unit"> kcal</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">Exercise</div>
              <div className="value">
                {todayHealth.exerciseMinutes ?? '–'}
                <span className="unit"> min</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- today ---- */}
      <div className="card accent">
        <div className="card-title" style={{ color: 'var(--accent)' }}>
          {plan.scheduled === 'lift'
            ? 'Today — lifting'
            : plan.scheduled === 'cardio'
              ? 'Today — cardio'
              : 'Today — rest'}
        </div>
        <h1>{plan.headline}</h1>
        <p className="muted small" style={{ marginTop: 6 }}>
          {plan.subline}
        </p>

        {plan.recommendedLift && !plan.loggedLift && (
          <>
            <div className="note small" style={{ margin: '12px 0' }}>
              {plan.recommendedLift.reason}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn primary lg grow"
                onClick={() => begin(plan.recommendedLift!.template)}
              >
                {plan.scheduled === 'lift' ? 'Start workout' : 'Train anyway'}
              </button>
              <button className="btn ghost lg" onClick={() => setPreview(plan.recommendedLift!.template)}>
                Preview
              </button>
            </div>
            {plan.liftOptions.length > 1 && (
              <button
                className="btn ghost sm block"
                style={{ marginTop: 8 }}
                onClick={() => setShowAlternatives(true)}
              >
                Do a different session instead
              </button>
            )}
          </>
        )}

        {plan.loggedLift && (
          <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
            <span className="pill ok">{plan.loggedLift.exercises.length} exercises logged</span>
            <span className="pill">
              {plan.loggedLift.exercises.reduce((n, e) => n + e.sets.length, 0)} sets
            </span>
          </div>
        )}
      </div>

      {/* ---- warnings ---- */}
      {plan.warnings.map((w, i) => (
        <div className="warning" key={i}>
          <IconInfo size={15} />
          <span>{w}</span>
        </div>
      ))}

      {/* ---- cardio ---- */}
      {plan.cardio && (plan.scheduled === 'cardio' || plan.scheduled === 'rest') && (
        <div className="card">
          <div className="card-title">
            {plan.scheduled === 'cardio' ? "Today's cardio" : 'Optional cardio'}
          </div>
          <div className="row between">
            <div className="grow">
              <h3>{plan.cardio.type.name}</h3>
              <div className="small muted">{plan.cardio.type.defaultIntensity}</div>
            </div>
            <span className="pill accent">
              <IconFlame size={13} />~{plan.cardio.estimatedCalories} kcal
            </span>
          </div>
          <div className="grid-3" style={{ marginTop: 11 }}>
            <div className="stat">
              <div className="label">Time</div>
              <div className="value">
                {plan.cardio.minutes}
                <span className="unit"> min</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">Zone</div>
              <div className="value" style={{ fontSize: 16 }}>
                {plan.cardio.zoneLabel.split(' — ')[0]}
              </div>
            </div>
            <div className="stat">
              <div className="label">Heart rate</div>
              <div className="value" style={{ fontSize: 16 }}>
                {plan.cardio.zoneBpm[0]}–{plan.cardio.zoneBpm[1]}
              </div>
            </div>
          </div>
          <div className="note small" style={{ marginTop: 11 }}>
            {plan.cardio.reason}
          </div>
          <button className="btn block" style={{ marginTop: 11 }} onClick={onGoCardio}>
            Log cardio
          </button>
        </div>
      )}

      {/* ---- recovery snapshot ---- */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 10 }}>
          <span className="card-title" style={{ margin: 0 }}>
            Most rested
          </span>
          <button className="btn ghost sm" onClick={() => setShowRecovery(true)}>
            See all
          </button>
        </div>
        <div className="row wrap" style={{ gap: 6 }}>
          {overdue.map((s) => (
            <span className="pill" key={s.muscle}>
              {MUSCLES[s.muscle].short}
              <span className="faint">
                {s.daysSince === undefined ? 'never' : `${s.daysSince}d`}
              </span>
            </span>
          ))}
        </div>
      </div>

      <Sheet open={showRecovery} title="Recovery" onClose={() => setShowRecovery(false)}>
        <RecoveryGrid statuses={plan.statuses} />
        <div className="tiny faint" style={{ marginTop: 14 }}>
          A full bar means that muscle has had its typical recovery window since
          you last trained it directly. It is an estimate from your logged
          sessions, not a measurement — if you feel wrecked, believe yourself
          over the bar.
        </div>
      </Sheet>

      <Sheet
        open={showAlternatives}
        title="Pick a session"
        onClose={() => setShowAlternatives(false)}
      >
        <div className="stack" style={{ gap: 9 }}>
          {plan.liftOptions.map((option) => (
            <SessionOptionRow key={option.template.id} option={option} onStart={begin} />
          ))}
        </div>
      </Sheet>

      <PreviewSheet template={preview} onClose={() => setPreview(null)} onStart={begin} />
    </div>
  );
}

function SessionOptionRow({
  option,
  onStart,
}: {
  option: SessionOption;
  onStart: (t: SessionTemplate) => void;
}) {
  return (
    <div className="card" style={{ padding: 13 }}>
      <div className="row between">
        <div className="grow">
          <h3>{option.template.name}</h3>
          <div className="tiny muted">{option.template.focus}</div>
        </div>
        <span className={`pill ${option.freshness >= 1 ? 'ok' : option.underRecovered ? 'warn' : ''}`}>
          {Math.round(option.freshness * 100)}%
        </span>
      </div>
      <div className="tiny faint" style={{ margin: '8px 0 10px' }}>
        {option.reason}
      </div>
      <button className="btn sm block" onClick={() => onStart(option.template)}>
        Start {option.template.name}
      </button>
    </div>
  );
}

/** Shows exactly what the generator picked before you commit to the session. */
function PreviewSheet({
  template,
  onClose,
  onStart,
}: {
  template: SessionTemplate | null;
  onClose: () => void;
  onStart: (t: SessionTemplate) => void;
}) {
  const { state } = useApp();

  const generated = useMemo(() => {
    if (!template) return null;
    return generateWorkout({
      template,
      equipment: new Set(state.settings.equipment),
      workouts: state.workouts,
      prefs: state.exercisePrefs,
      block: planDay(state).block,
      goal: state.profile.goal,
      useSupersets: state.settings.useSupersets,
    });
  }, [template, state]);

  if (!template || !generated) return null;

  const totalSets = generated.exercises.reduce((n, e) => n + e.targetSets, 0);

  return (
    <Sheet open={Boolean(template)} title={template.name} onClose={onClose}>
      <div className="row wrap" style={{ gap: 6, marginBottom: 12 }}>
        <span className="pill">{generated.exercises.length} exercises</span>
        <span className="pill">{totalSets} working sets</span>
      </div>

      <div className="stack" style={{ gap: 7 }}>
        {generated.exercises.map((le, i) => {
          const ex = getExercise(le.exerciseId);
          if (!ex) return null;
          return (
            <div className="row between" key={`${le.exerciseId}-${i}`} style={{ gap: 10 }}>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{ex.name}</div>
                <div className="tiny faint">
                  {le.targetSets} × {le.targetReps[0]}–{le.targetReps[1]}
                  {le.suggestionKg ? ` · ${le.suggestionKg} kg` : ''}
                </div>
              </div>
              <span className="pill">{ex.primary.map((m) => MUSCLES[m].short).join(', ')}</span>
            </div>
          );
        })}
      </div>

      {generated.notes.map((note, i) => (
        <div className="note small" key={i} style={{ marginTop: 12 }}>
          {note}
        </div>
      ))}

      <button className="btn primary lg block" style={{ marginTop: 16 }} onClick={() => onStart(template)}>
        Start this workout
      </button>
    </Sheet>
  );
}
